import React from "react";
import { 
  FaLock, 
  FaUserShield, 
  FaDatabase, 
  FaCookie, 
  FaShieldAlt, 
  FaEnvelope, 
  FaFileAlt, 
  FaGlobe 
} from "react-icons/fa";

export default function PrivacyPolicy() {
  return (
    <div className="py-12 px-4 md:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 px-6 py-8">
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-gray-200">
            Last Updated: May 10, 2025
          </p>
        </div>
        
        <div className="p-6 md:p-8">
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-gray-700 mb-4">
              At MASHO Perfumes, we value your privacy and are committed to protecting your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit our website,
              make a purchase, or interact with us in any way.
            </p>
            <p className="text-gray-700">
              By using our services, you consent to the practices described in this policy.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaDatabase className="mr-2 text-yellow-300" /> Information We Collect
            </h2>
            <div className="ml-1">
              <h3 className="font-medium text-gray-800 mt-4 mb-2">Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li><strong>Contact Details:</strong> Name, email address, phone number</li>
                <li><strong>Shipping Information:</strong> Delivery address and instructions</li>
                <li><strong>Payment Details:</strong> Credit card information (processed securely by our payment provider)</li>
                <li><strong>Order History:</strong> Products purchased, order dates, and frequency</li>
                <li><strong>Customer Service:</strong> Communications when you contact our support team</li>
              </ul>

              <h3 className="font-medium text-gray-800 mt-4 mb-2">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li><strong>Device Information:</strong> Browser type, IP address, device type</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent, referring website</li>
                <li><strong>Location Data:</strong> General location based on IP address</li>
              </ul>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaFileAlt className="mr-2 text-yellow-300" /> How We Use Your Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2">Order Processing</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Process and fulfill your purchases</li>
                  <li>Shipping and delivery management</li>
                  <li>Payment processing</li>
                  <li>Order updates and notifications</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2">Customer Experience</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Personalize your shopping experience</li>
                  <li>Remember your preferences</li>
                  <li>Provide customer support</li>
                  <li>Respond to your inquiries</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2">Marketing</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Send promotional emails (with opt-out option)</li>
                  <li>Special offers and discounts</li>
                  <li>Product recommendations</li>
                  <li>Newsletter subscriptions</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2">Business Operations</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>Improve our website and products</li>
                  <li>Analyze usage patterns</li>
                  <li>Detect and prevent fraud</li>
                  <li>Legal compliance</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cookies & Tracking */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaCookie className="mr-2 text-yellow-300" /> Cookies & Tracking Technologies
            </h2>
            <p className="text-gray-700 mb-3">
              We use cookies and similar technologies to enhance your browsing experience and analyze website traffic.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Types of Cookies We Use</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-1">
                  <li><strong>Essential Cookies:</strong> Required for site functionality</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                  <li><strong>Marketing Cookies:</strong> Track effectiveness of marketing campaigns</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Cookie Management</h3>
                <p className="text-gray-600 text-sm">
                  You can manage cookie preferences through your browser settings. Disabling certain cookies may impact site functionality.
                </p>
                <div className="mt-3 p-3 bg-gray-100 rounded text-sm text-gray-600">
                  <p>To learn more about cookies and how to manage them, visit <span className="text-yellow-300">www.allaboutcookies.org</span></p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Data Sharing */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaShieldAlt className="mr-2 text-yellow-300" /> Information Sharing
            </h2>
            <p className="text-gray-700 mb-3">
              We share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Service Providers:</strong> Trusted third parties who help us operate our business (shipping carriers, payment processors, email services)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have explicitly agreed to the sharing</li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-300 p-4 mt-4">
              <p className="text-gray-700"><strong>Our Commitment:</strong> We never sell your personal information to third parties for marketing purposes.</p>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaLock className="mr-2 text-yellow-300" /> Data Security
            </h2>
            <p className="text-gray-700 mb-3">
              We implement appropriate security measures to protect your personal information:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaLock size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Encryption</h3>
                  <p className="text-gray-600 text-sm">All sensitive data is encrypted using SSL/TLS technology</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaUserShield size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Access Controls</h3>
                  <p className="text-gray-600 text-sm">Limited employee access to personal information</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaShieldAlt size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Regular Audits</h3>
                  <p className="text-gray-600 text-sm">Continuous monitoring and security testing</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg flex">
                <div className="mr-3 text-yellow-300"><FaGlobe size={24} /></div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Data Protection</h3>
                  <p className="text-gray-600 text-sm">Compliance with data protection regulations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaUserShield className="mr-2 text-yellow-300" /> Your Privacy Rights
            </h2>
            <p className="text-gray-700 mb-3">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to processing of your information</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Portability:</strong> Request transfer of your information</li>
              <li><strong>Withdrawal:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-700 mt-3">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </div>

          {/* Policy Updates */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center border-b pb-2">
              <FaFileAlt className="mr-2 text-yellow-300" /> Changes to This Policy
            </h2>
            <p className="text-gray-700">
              We may update this Privacy Policy periodically to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page with a new effective date. We encourage you to review this policy regularly.
            </p>
          </div>

          {/* Contact Us */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaEnvelope className="mr-2 text-yellow-300" /> Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-medium text-gray-800 mb-2">Email</h3>
                <p className="text-yellow-300">privacy@mashoperfumes.com</p>
              </div>
              <div className="bg-white p-4 rounded shadow-sm">
                <h3 className="font-medium text-gray-800 mb-2">Mail</h3>
                <p className="text-gray-600">
                  MASHO Perfumes<br />
                  Malappuram, Kerala, India - 676519
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}